import { useMemo, useState } from 'react'
import { formatClock, useTimeFormat } from '../../lib/timeFormat'
import { buildAssignmentSheet, sheetHeadcount, timeRange } from './sheetFigures'
import { assignmentSheetTable } from './assignmentSheetExport'
import { getApprovedLeaveOn, getSheetEntries } from './assignmentSheetStore'
import { toReportRows } from './coverageReport'
import ExportBar from './ExportBar'
import { ReportSelect } from './ReportControls'
import { fullDate, todayIso } from './reportDates'
import { useCoverageData } from './useCoverageData'
import { useLoaded } from './useLoaded'

/**
 * docs/product/reports.md's daily assignment sheet: the daily assignment
 * board for one date and one team as a page to post or hand out at the start
 * of a shift. On paper it is the sheet alone.
 */
const FIELD_CLASS = 'min-h-11 w-full rounded-control border border-line-strong bg-canvas px-3 text-small'

export default function AssignmentSheet() {
  const { format } = useTimeFormat()
  const [date, setDate] = useState(todayIso())
  const [chosenTeam, setChosenTeam] = useState('')
  const sheetData = useLoaded(
    async () => {
      if (!date) return { entries: [], leave: new Set<string>() }
      const [entries, leave] = await Promise.all([getSheetEntries(date), getApprovedLeaveOn(date)])
      return { entries, leave }
    },
    { entries: [], leave: new Set<string>() },
    [date],
  )
  const coverage = useCoverageData(date, date)
  const { lookups } = coverage
  const loading = sheetData.loading || coverage.loading
  const failed = sheetData.failed || coverage.failed

  const teamIds = useMemo(
    () => Array.from(new Set(sheetData.data.entries.map((entry) => entry.teamId))).sort((a, b) => (lookups.teams.get(a) ?? '').localeCompare(lookups.teams.get(b) ?? '')),
    [sheetData.data.entries, lookups],
  )
  const teamId = teamIds.includes(chosenTeam) ? chosenTeam : (teamIds[0] ?? '')
  const teamName = lookups.teams.get(teamId) ?? ''
  const coverageRows = useMemo(() => toReportRows(coverage.rows, lookups), [coverage.rows, lookups])
  const groups = useMemo(
    () => buildAssignmentSheet(sheetData.data.entries, teamId, sheetData.data.leave, coverageRows),
    [sheetData.data, teamId, coverageRows],
  )
  const headcount = sheetHeadcount(groups)
  const clock = (time: string) => formatClock(time, format)

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Report controls" className="rounded-card border border-line bg-canvas p-4 print:hidden">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1 text-caption font-bold text-ink-muted">
            Date
            <input type="date" value={date} onChange={(event) => setDate(event.target.value)} className={FIELD_CLASS} />
          </label>
          <label className="flex flex-col gap-1 text-caption font-bold text-ink-muted">
            Team
            <ReportSelect value={teamId} onChange={(event) => setChosenTeam(event.target.value)} disabled={teamIds.length === 0}>
              {teamIds.length === 0 && <option value="">No team</option>}
              {teamIds.map((id) => (
                <option key={id} value={id}>
                  {lookups.teams.get(id) ?? 'Unknown team'}
                </option>
              ))}
            </ReportSelect>
          </label>
        </div>
      </section>

      <ExportBar
        count={`${headcount} on duty`}
        loading={loading}
        disabled={loading || failed || headcount === 0}
        fileStem={`muster-assignment-sheet-${date}`}
        table={() => assignmentSheetTable(groups, { date, team: teamName, generatedAt: new Date() })}
      >
        <button
          type="button"
          disabled={loading || failed || headcount === 0}
          onClick={() => window.print()}
          className="min-h-11 rounded-control border border-primary bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-50"
        >
          Print
        </button>
      </ExportBar>

      {failed ? (
        <p role="alert" className="rounded-card border border-line bg-canvas p-4 text-small text-error">
          The assignment sheet could not be loaded. Reload the page, or try again in a moment.
        </p>
      ) : loading ? (
        <div aria-hidden="true" className="flex flex-col gap-2 rounded-card border border-line bg-canvas p-4">
          {[0, 1, 2, 3].map((line) => (
            <div key={line} className="h-8 animate-pulse rounded-control bg-surface-2" />
          ))}
        </div>
      ) : (
        <article aria-label="Daily assignment sheet" className="rounded-card border border-line bg-canvas p-4 print:border-0 print:p-0 print:text-black">
          <header className="mb-4 border-b border-line pb-3">
            <h2 className="text-h3 font-extrabold">{teamName ? `${teamName} daily assignments` : 'Daily assignments'}</h2>
            <p className="text-small text-ink-muted print:text-black">{date ? fullDate(date) : ''}</p>
          </header>
          {groups.length === 0 ? (
            <p className="py-6 text-center text-small text-ink-muted">No one is on duty for this team on this date. Choose another date or team.</p>
          ) : (
            <div className="flex flex-col gap-5">
              {groups.map((group) => (
                <section key={group.key} aria-label={group.assignment} className="print:break-inside-avoid">
                  <h3 className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line-strong pb-1 text-body font-bold">
                    <span>{group.assignment}</span>
                    <span className="text-small font-normal text-ink-muted print:text-black">
                      {group.people.length} assigned{group.required !== null ? `, ${group.required} required` : ''}
                    </span>
                  </h3>
                  <table className="w-full border-collapse">
                    <caption className="sr-only">{group.assignment}</caption>
                    <thead>
                      <tr className="text-left">
                        <th scope="col" className="px-2 py-1.5 text-caption font-bold tracking-wide text-ink-subtle uppercase print:text-black">Employee</th>
                        <th scope="col" className="px-2 py-1.5 text-caption font-bold tracking-wide text-ink-subtle uppercase print:text-black">Shift</th>
                        <th scope="col" className="px-2 py-1.5 text-caption font-bold tracking-wide text-ink-subtle uppercase print:text-black">Activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.people.map((person) => (
                        <tr key={person.employeeId} className="border-b border-line last:border-b-0">
                          <th scope="row" className="px-2 py-1.5 text-left text-small font-semibold">{person.employee}</th>
                          <td className="px-2 py-1.5 text-small [font-variant-numeric:tabular-nums]">{timeRange(person, clock)}</td>
                          <td className="px-2 py-1.5 text-small">{person.activity ?? 'None'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              ))}
            </div>
          )}
        </article>
      )}
    </div>
  )
}
